import { DOCUMENT, NgClass } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  ElementRef,
  inject,
  input,
  output,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { TranslateModule } from '@ngx-translate/core';
import { fromEvent } from 'rxjs';
import { filter, map } from 'rxjs/operators';

@Component({
  selector: 'ish-in-place-edit',
  templateUrl: './in-place-edit.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrls: ['./in-place-edit.component.scss'],
  imports: [FaIconComponent, TranslateModule, NgClass],
})
export class InPlaceEditComponent implements AfterViewInit {
  private host = inject(ElementRef);
  private cdRef = inject(ChangeDetectorRef);
  private document = inject<Document>(DOCUMENT);

  // localization key, can be used to give the edit-pen icon a more descriptive aria label that describes what will be edited when clicking it
  readonly ariaLabelName = input('');
  readonly buttonClass = input('');
  readonly confirmDisabled = input(false);
  readonly edited = output<void>();
  readonly aborted = output<void>();

  private mode: 'view' | 'edit' = 'view';
  private destroyRef = inject(DestroyRef);

  // change into edit mode by clicking on the text
  ngAfterViewInit() {
    fromEvent(this.document, 'mousedown')
      .pipe(
        // toggle edit mode only on main button click
        filter((event: MouseEvent) => !event.button),
        map(({ target }) => this.host.nativeElement.contains(target)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(click => this.toggleEditMode(click));
  }

  toggleEditMode(edit: boolean) {
    const newMode = edit ? 'edit' : 'view';
    if (newMode === 'view' && newMode !== this.mode) {
      this.confirm();
    }
    this.mode = newMode;
    this.cdRef.detectChanges();
    if (this.mode === 'edit') {
      setTimeout(() => {
        this.host.nativeElement.querySelector('.form-control')?.focus();
      }, 200);
    }
  }

  confirm() {
    if (this.confirmDisabled()) {
      return;
    }
    this.mode = 'view';
    // TODO: The 'emit' function requires a mandatory void argument
    this.edited.emit();
  }

  cancel() {
    this.mode = 'view';
    // TODO: The 'emit' function requires a mandatory void argument
    this.aborted.emit();
  }

  get viewMode() {
    return this.mode === 'view';
  }
}
